import type { PanderrGovernmentPage, PanderrSection } from "../types";

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function renderGovernmentHubSection(
  section: PanderrSection,
): string {
  const page = section.governmentPage || "overview";
  const heading =
    section.heading?.trim() ||
    "Government";

  const body =
    section.body?.trim() ||
    "Find information about elected officials, administration, public meetings, budgets, projects, notices, and public records.";

  const panels: Record<PanderrGovernmentPage, string> = {
    overview: `<section id="overview" class="panderr-government-panel panderr-government-overview" data-panderr-managed-content="government-overview">
      <p class="panderr-government-eyebrow">Local Government</p>
      <h1>${escapeHtml(heading)}</h1>
      <p class="panderr-government-lead">${escapeHtml(body)}</p>
    </section>`,
    "mayor-council": `<section id="officials" class="panderr-government-panel" data-panderr-managed-content="government-officials">
      <p class="panderr-government-eyebrow">Elected Officials</p>
      <h1>Mayor & Council</h1>
      <p class="panderr-government-lead">Meet the elected officials serving the community and learn about their roles and responsibilities.</p>
    </section>`,
    "agendas-minutes": `<section id="agendas" class="panderr-government-panel" data-panderr-managed-content="government-meetings">
      <p class="panderr-government-eyebrow">Public Meetings</p>
      <h1>Agendas & Minutes</h1>
      <p class="panderr-government-lead">View Town Council meeting agendas, approved minutes, and supporting documents.</p>
      <div class="panderr-government-managed-slot" data-panderr-government-meetings-slot="true"></div>
    </section>`,
    "budgets-projects": `<section id="budgets" class="panderr-government-panel" data-panderr-managed-content="government-budgets">
      <p class="panderr-government-eyebrow">Financial Information</p>
      <h1>Budgets & Projects</h1>
      <p class="panderr-government-lead">Review municipal budgets, capital projects, financial information, and major initiatives.</p>
    </section>`,
    "public-notices": `<section id="notices" class="panderr-government-panel" data-panderr-managed-content="government-notices">
      <p class="panderr-government-eyebrow">Public Information</p>
      <h1>Public Notices</h1>
      <p class="panderr-government-lead">View official notices, public hearings, community advisories, and government announcements.</p>
    </section>`,
    "open-records": `<section id="open-records" class="panderr-government-panel" data-panderr-managed-content="government-open-records">
      <p class="panderr-government-eyebrow">Transparency</p>
      <h1>Open Records</h1>
      <p class="panderr-government-lead">Request public records and review information about the municipality's open-records process.</p>
    </section>`,
    "public-participation": `<section id="public-participation" class="panderr-government-panel" data-panderr-managed-content="government-public-participation">
      <p class="panderr-government-eyebrow">Community Engagement</p>
      <h1>Public Participation</h1>
      <p class="panderr-government-lead">Request public engagement or participation with the Town of Lone Oak.</p>
    </section>`,
  };

  const labels: Record<PanderrGovernmentPage, string> = {
    overview: "Government Overview",
    "mayor-council": "Mayor & Council",
    "agendas-minutes": "Agendas & Minutes",
    "budgets-projects": "Budgets & Projects",
    "public-notices": "Public Notices",
    "open-records": "Open Records",
    "public-participation": "Public Participation",
  };

  return `
    <section
      class="panderr-government-hub"
      data-panderr-section-type="government-hub"
      data-panderr-section-id="${escapeHtml(section.id)}"
    >
      <div class="panderr-shell-wide panderr-government-hub-grid">
        <aside
          class="panderr-government-sidebar"
          aria-label="Government navigation"
        >
          <div class="panderr-government-sidebar-title">
            Government
          </div>

          <nav class="panderr-government-sidebar-nav">
            ${[
              ["/government/", "Government Overview", "overview"],
              ["/government/mayor-council/", "Mayor & Council", "mayor-council"],
              ["/government/agendas-minutes/", "Agendas & Minutes", "agendas-minutes"],
              ["/government/budgets-projects/", "Budgets & Projects", "budgets-projects"],
              ["/government/public-notices/", "Public Notices", "public-notices"],
              ["/government/open-records/", "Open Records", "open-records"],
              ["/government/public-participation/", "Public Participation", "public-participation"],
            ].map(([href, label, itemPage]) => `
              <a href="${href}"${page === itemPage ? ' aria-current="page"' : ''}>${label}</a>
            `).join("")}
          </nav>
        </aside>

        <div class="panderr-government-main">
          <nav
            class="panderr-government-breadcrumbs"
            aria-label="Breadcrumb"
          >
            <a href="/">Home</a>
            <span aria-hidden="true">›</span>
            <span>${escapeHtml(labels[page])}</span>
          </nav>
          <div class="panderr-government-main-content">
            ${panels[page]}
          </div>
        </div>
      </div>
    </section>
  `;
}

export function createGovernmentHubCss(): string {
  return `
    /*
     * Municipality Modern
     * Civic presentation layer
     */

    body[data-panderr-template-id="municipality-modern"] {
      --municipal-navy: #173f69;
      --municipal-navy-dark: #102f50;
      --municipal-blue-soft: #eef4f8;
      --municipal-gold: #b99a55;
      --municipal-border: #dce3e8;
      --municipal-text: #263746;
      --municipal-muted: #667583;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-header {
      border-bottom: 0;
      background: #fff;
      box-shadow: 0 1px 0 rgba(23, 63, 105, .12);
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-header-inner {
      min-height: 94px;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-text-brand {
      max-width: 250px;
      color: var(--municipal-navy);
      font-size: 1.08rem;
      line-height: 1.18;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav {
      font-size: .92rem;
      font-weight: 700;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav a,
    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav-dropdown summary {
      color: #465868;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav a:hover,
    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav-dropdown summary:hover,
    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav-dropdown[open] summary {
      color: var(--municipal-navy);
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav-dropdown-menu {
      min-width: 250px;
      padding: .55rem;
      border: 1px solid var(--municipal-border);
      border-radius: 6px;
      box-shadow: 0 18px 45px rgba(24, 49, 72, .16);
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-nav-dropdown-menu a {
      padding: .8rem .9rem;
      border-radius: 4px;
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-header-cta {
      min-height: 44px;
      border-radius: 4px;
      background: var(--municipal-navy);
    }

    body[data-panderr-template-id="municipality-modern"]
    .panderr-header-cta:hover {
      background: var(--municipal-navy-dark);
    }


    /* Government page */

    .panderr-government-hub {
      padding: 1.75rem 0 6rem;
      background:
        linear-gradient(180deg, var(--municipal-blue-soft) 0, #f6f8fa 420px, #f6f8fa 100%);
    }

    .panderr-government-hub-grid {
      display: grid;
      grid-template-columns: 270px minmax(0, 1fr);
      gap: clamp(1.75rem, 3.4vw, 3rem);
      align-items: start;
    }


    /* Sidebar */

    .panderr-government-sidebar {
      position: sticky;
      top: 1.5rem;
      z-index: 2;
      overflow: hidden;
      border: 1px solid var(--municipal-border);
      border-top: 3px solid var(--municipal-navy);
      border-radius: 10px;
      background: #fff;
      box-shadow: 0 1px 2px rgba(23, 63, 105, .06);
    }

    .panderr-government-sidebar-title {
      padding: 1.15rem 1.25rem;
      color: var(--municipal-navy);
      font-size: .78rem;
      font-weight: 800;
      letter-spacing: .14em;
      text-transform: uppercase;
    }

    .panderr-government-sidebar-nav {
      display: flex;
      flex-direction: column;
    }

    .panderr-government-sidebar-nav a {
      position: relative;
      display: block;
      padding: .94rem 1.15rem;
      border-bottom: 1px solid #e7ecef;
      color: var(--municipal-text);
      font-size: .93rem;
      font-weight: 700;
      line-height: 1.25;
      text-decoration: none;
      transition:
        background var(--panderr-transition-fast),
        color var(--panderr-transition-fast),
        padding var(--panderr-transition-fast);
    }

    .panderr-government-sidebar-nav a:last-child {
      border-bottom: 0;
    }

    .panderr-government-sidebar-nav a:hover,
    .panderr-government-sidebar-nav a:focus-visible,
    .panderr-government-sidebar-nav a[aria-current="page"] {
      padding-left: 1.35rem;
      background: var(--municipal-blue-soft);
      color: var(--municipal-navy);
    }

    .panderr-government-sidebar-nav a:hover::before,
    .panderr-government-sidebar-nav a:focus-visible::before,
    .panderr-government-sidebar-nav a[aria-current="page"]::before {
      content: "";
      position: absolute;
      inset: 0 auto 0 0;
      width: 4px;
      background: var(--municipal-gold);
    }


    /* Main content */

    .panderr-government-main {
      min-width: 0;
      padding: 1.9rem clamp(1.4rem, 3vw, 2.6rem) 2.75rem;
      border: 1px solid var(--municipal-border);
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 1px 3px rgba(23, 63, 105, .05);
    }

    .panderr-government-breadcrumbs {
      display: flex;
      align-items: center;
      gap: .5rem;
      min-height: 24px;
      margin-bottom: 1.1rem;
      color: var(--municipal-muted);
      font-size: .86rem;
      font-weight: 650;
    }

    .panderr-government-breadcrumbs a {
      color: var(--municipal-navy);
    }

    .panderr-government-breadcrumbs a:hover {
      text-decoration: underline;
    }


    /* Government intro */

    .panderr-government-overview {
      margin-bottom: 2.2rem !important;
      padding: 0 0 2.2rem !important;
      border-bottom: 0 !important;
    }

    .panderr-government-overview h1 {
      margin: 0 0 1rem;
      color: var(--municipal-navy);
      font-size: clamp(3rem, 5.5vw, 5rem);
      line-height: .98;
      letter-spacing: -.045em;
    }

    .panderr-government-overview
    .panderr-government-lead {
      max-width: 760px;
      color: var(--municipal-muted);
      font-size: clamp(1rem, 1.35vw, 1.13rem);
      line-height: 1.7;
    }


    /* Content sections */

    .panderr-government-panel {
      scroll-margin-top: 2rem;
      margin: 0 0 3.5rem;
      padding: 0 0 3.5rem;
      border-bottom: 1px solid var(--municipal-border);
    }

    .panderr-government-panel:last-child {
      margin-bottom: 0;
      border-bottom: 0;
    }

    .panderr-government-panel h2 {
      margin: 0 0 .85rem;
      color: var(--municipal-navy);
      font-size: clamp(2rem, 3vw, 2.65rem);
      line-height: 1.08;
      letter-spacing: -.035em;
    }

    .panderr-government-panel h1 {
      margin: 0 0 1rem;
      color: var(--municipal-navy);
      font-size: clamp(2.45rem, 4vw, 4rem);
      line-height: 1.02;
      letter-spacing: -.04em;
    }

    .panderr-government-panel > p {
      max-width: 760px;
      color: var(--municipal-muted);
      line-height: 1.75;
    }

    .panderr-government-eyebrow {
      margin: 0 0 .55rem;
      color: var(--municipal-navy);
      font-size: .75rem;
      font-weight: 850;
      letter-spacing: .13em;
      text-transform: uppercase;
    }

    .panderr-government-panel-header {
      display: flex;
      gap: 2rem;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: .8rem;
    }

    .panderr-government-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      padding: .75rem 1rem;
      border: 1px solid var(--municipal-navy);
      border-radius: 4px;
      background: var(--municipal-navy);
      color: #fff !important;
      font-size: .9rem;
      font-weight: 750;
      text-decoration: none;
      white-space: nowrap;
      transition:
        background var(--panderr-transition-fast),
        transform var(--panderr-transition-fast);
    }

    .panderr-government-action:hover {
      background: var(--municipal-navy-dark);
      transform: translateY(-1px);
    }

    .panderr-government-managed-slot {
      margin-top: 1.7rem;
    }

    .panderr-government-main-content .lo-officials {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1.5rem;
      margin-top: 2rem;
    }

    .panderr-government-main-content .lo-official-card {
      overflow: hidden;
      border: 1px solid var(--municipal-border);
      border-radius: 8px;
      background: #fff;
    }

    .panderr-government-main-content .lo-official-card img {
      display: block;
      width: 100%;
      aspect-ratio: 4 / 5;
      max-height: none;
      object-fit: cover;
      object-position: center top;
    }

    .panderr-government-main-content .lo-official-copy {
      padding: 1.25rem;
    }

    .panderr-government-main-content .lo-official-copy p {
      margin: .45rem 0 0;
    }

    .panderr-government-main-content .lo-official-bio {
      margin-top: 1rem !important;
      max-width: 60ch;
      line-height: 1.7;
    }

    .panderr-government-hub .lo-list {
      max-width: 820px;
      margin-top: 2rem;
    }

    .panderr-government-hub .lo-list h2 {
      margin: 0 0 .75rem;
      color: var(--municipal-navy);
      font-size: 1.15rem;
      letter-spacing: .08em;
      text-transform: uppercase;
    }

    .panderr-government-hub .lo-list article {
      margin: 0;
      padding: 1.25rem 0;
      border: 0;
      border-top: 1px solid var(--municipal-border);
      border-radius: 0;
      background: transparent;
    }

    .panderr-government-hub .lo-list article:last-child {
      border-bottom: 1px solid var(--municipal-border);
    }

    .panderr-government-hub .lo-list h3 {
      margin: .35rem 0 .5rem;
      color: var(--municipal-navy);
      font-size: 1.25rem;
    }

    .panderr-government-hub .lo-list .lo-summary {
      max-width: 70ch;
      line-height: 1.7;
    }

    .panderr-government-hub .lo-participation-form {
      max-width: 760px;
      margin-left: 0;
    }


    /* Tables inserted by managed municipal content */

    .panderr-government-hub table {
      width: 100%;
      margin-top: 1.5rem;
      border-collapse: collapse;
      border: 1px solid var(--municipal-border);
      background: #fff;
    }

    .panderr-government-hub th {
      padding: .9rem 1rem;
      background: var(--municipal-navy);
      color: #fff;
      text-align: left;
      font-size: .82rem;
      letter-spacing: .025em;
    }

    .panderr-government-hub td {
      padding: 1rem;
      border-top: 1px solid var(--municipal-border);
      color: var(--municipal-text);
      vertical-align: top;
    }

    .panderr-government-hub tbody tr:nth-child(even) {
      background: #f8fafb;
    }


    @media (max-width: 1040px) {
      .panderr-government-hub-grid {
        grid-template-columns: 235px minmax(0, 1fr);
        gap: 2.2rem;
      }
    }

    @media (max-width: 860px) {
      .panderr-government-hub {
        padding-top: 1.5rem;
      }

      .panderr-government-hub-grid {
        grid-template-columns: 1fr;
        padding-top: 34px;
      }

      .panderr-government-sidebar {
        position: static;
        order: 2;
        margin-bottom: 1rem;
      }

      .panderr-government-main {
        order: 1;
      }

      .panderr-government-sidebar-nav {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .panderr-government-sidebar-nav a {
        border-right: 1px solid #e7ecef;
      }

      .panderr-government-overview {
        min-height: 0;
      }
    }

    @media (max-width: 600px) {
      .panderr-government-hub {
        padding-bottom: 4rem;
      }

      .panderr-government-hub-grid {
        width: min(calc(100% - 2rem), var(--panderr-wide-max-width));
      }

      .panderr-government-sidebar-nav {
        grid-template-columns: 1fr;
      }

      .panderr-government-panel-header {
        flex-direction: column;
        gap: 1rem;
      }

      .panderr-government-overview h1 {
        font-size: clamp(2.65rem, 14vw, 4rem);
      }

      .panderr-government-main-content .lo-officials {
        grid-template-columns: 1fr;
      }

      .panderr-government-panel h2 {
        font-size: 2rem;
      }

      .panderr-government-action {
        white-space: normal;
      }

      .panderr-government-hub table,
      .panderr-government-hub thead,
      .panderr-government-hub tbody,
      .panderr-government-hub tr,
      .panderr-government-hub th,
      .panderr-government-hub td {
        display: block;
        width: 100%;
      }

      .panderr-government-hub thead {
        display: none;
      }

      .panderr-government-hub tr {
        padding: .5rem 0;
        border-bottom: 1px solid var(--municipal-border);
      }

      .panderr-government-hub td {
        border: 0;
        padding: .55rem 1rem;
      }
    }
  `;
}


